/**
 * [T149-PR9-FE] RecurringVsDiscretionarySkin — `recurring-vs-discretionary` adapter.
 *
 * Resolver: recurringVsOneOff — returns recurring/oneOff spending split with percentage shares.
 * Layout:
 *   - isTooSmall  → 2 KPI percentage numbers side-by-side
 *   - isMobile    → 2 stacked rows with labelled progress bars + amount
 *   - desktop     → vertical BarChart comparing recurring.total vs oneOff.total
 *
 * @see https://recharts.org/en-US/api/BarChart
 * @see docs/design/T149-PR5-INSIGHTS-SKINS-SPEC.md §3.4
 */
import { Repeat2 } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { useWindowSize } from '@/hooks/useWindowSize';
import { useCurrency } from '@/hooks/useCurrency';
import { WidgetFrame } from '../../WidgetFrame';
import type { SkinProps } from '../types';
import {
  skinTestId,
  SkinLoadingBars,
  SkinEmptyState,
  SkinErrorState,
  useSkinFrameProps,
} from './_shared';

const MIN_W = 2;
const MIN_H = 2;

const RECURRING_COLOR = 'hsl(var(--finance-negative))';
const ONETIME_COLOR = 'hsl(var(--chart-1))';

const CHART_CONFIG: ChartConfig = {
  value: { label: 'Amount' },
};

export default function RecurringVsDiscretionarySkin(props: SkinProps<'recurring-vs-discretionary'>) {
  const { widget, data, loading, error } = props;
  const { fmt } = useCurrency();
  const { width } = useWindowSize();
  const isMobile = (width ?? 1280) < 480;
  const isTooSmall = widget.w < MIN_W || widget.h < MIN_H;

  const recurring = data?.recurring;
  const oneOff = data?.oneOff;
  const totalCount = data?.totals?.count ?? 0;
  const isEmpty = !loading && !error && totalCount === 0;

  const chartData = [
    { name: 'Recurring', value: recurring?.total ?? 0 },
    { name: 'One-time', value: oneOff?.total ?? 0 },
  ];

  const frameProps = useSkinFrameProps(props);

  // Too-small fallback: 2 KPI percentages
  if (isTooSmall) {
    return (
      <WidgetFrame
        id={widget.id}
        title={widget.title || 'Recurring vs Discretionary'}
        icon={<Repeat2 className="h-4 w-4" />}
        {...frameProps}
        body={
          loading ? (
            <SkinLoadingBars bars={2} />
          ) : (
            <div className="flex gap-3 justify-around h-full items-center py-1">
              <div className="flex flex-col items-center gap-0.5">
                <span className="text-lg font-bold" style={{ color: RECURRING_COLOR }}>
                  {recurring ? `${recurring.percentage.toFixed(0)}%` : '—'}
                </span>
                <span className="text-[11px] text-muted-foreground truncate">Recurring</span>
              </div>
              <div className="flex flex-col items-center gap-0.5">
                <span className="text-lg font-bold" style={{ color: ONETIME_COLOR }}>
                  {oneOff ? `${oneOff.percentage.toFixed(0)}%` : '—'}
                </span>
                <span className="text-[11px] text-muted-foreground truncate">One-time</span>
              </div>
            </div>
          )
        }
      />
    );
  }

  // Mobile fallback: 2 stacked rows with progress bars
  if (isMobile) {
    return (
      <WidgetFrame
        id={widget.id}
        title={widget.title || 'Recurring vs Discretionary'}
        icon={<Repeat2 className="h-4 w-4" />}
        {...frameProps}
        body={
          loading ? (
            <SkinLoadingBars bars={2} />
          ) : isEmpty ? (
            <div data-testid={skinTestId('recurring-vs-discretionary', widget.id, 'empty')}>
              <SkinEmptyState
                title="No transactions in this period"
                description="Add transactions to see recurring vs one-time breakdown."
                cta={{ label: 'Add transaction', to: '/transactions' }}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-3 py-2 w-full">
              {(
                [
                  { label: 'Recurring', bucket: recurring, color: RECURRING_COLOR },
                  { label: 'One-time', bucket: oneOff, color: ONETIME_COLOR },
                ] as const
              ).map(({ label, bucket, color }) => (
                <div key={label} className="flex flex-col gap-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-foreground">{label}</span>
                    <span className="text-muted-foreground">
                      {bucket ? fmt(bucket.total) : '—'} · {bucket?.percentage.toFixed(0)}%
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${bucket?.percentage ?? 0}%`, backgroundColor: color }}
                      role="progressbar"
                      aria-valuenow={bucket?.percentage ?? 0}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  </div>
                </div>
              ))}
            </div>
          )
        }
      />
    );
  }

  // Desktop: vertical BarChart
  return (
    <WidgetFrame
      id={widget.id}
      title={widget.title || 'Recurring vs Discretionary'}
      icon={<Repeat2 className="h-4 w-4" />}
      {...frameProps}
      body={
        loading ? (
          <div data-testid={skinTestId('recurring-vs-discretionary', widget.id, 'loading')}>
            <SkinLoadingBars bars={2} />
          </div>
        ) : error ? (
          <div data-testid={skinTestId('recurring-vs-discretionary', widget.id, 'error')}>
            <SkinErrorState message={error.message} onRetry={props.onRetry} />
          </div>
        ) : isEmpty ? (
          <div data-testid={skinTestId('recurring-vs-discretionary', widget.id, 'empty')}>
            <SkinEmptyState
              title="No transactions in this period"
              description="Add transactions to see recurring vs one-time breakdown."
              cta={{ label: 'Add transaction', to: '/transactions' }}
            />
          </div>
        ) : (
          <div
            role="img"
            aria-label="Recurring vs one-time spending comparison"
            className="w-full h-full min-w-0 min-h-0 overflow-hidden"
            data-testid={skinTestId('recurring-vs-discretionary', widget.id)}
          >
            <ChartContainer config={CHART_CONFIG} className="h-full w-full overflow-hidden">
              <BarChart data={chartData} margin={{ left: 8, right: 16, top: 8, bottom: 8 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-border/60"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  tickFormatter={(v: number) =>
                    v >= 1_000_000
                      ? `${(v / 1_000_000).toFixed(0)}M`
                      : v >= 1000
                        ? `${(v / 1000).toFixed(0)}k`
                        : String(v)
                  }
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 10 }}
                  width={40}
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value) => [fmt(value as number), '']}
                    />
                  }
                />
                <Bar dataKey="value" barSize={48} radius={[4, 4, 0, 0]}>
                  <Cell fill={RECURRING_COLOR} />
                  <Cell fill={ONETIME_COLOR} />
                </Bar>
              </BarChart>
            </ChartContainer>
          </div>
        )
      }
    />
  );
}
